import { useEffect, useRef, useState } from 'react';
import driverLogo from '../../assets/big-taxi-driver-logo.png';
import { FileText } from 'lucide-react';
import { DriverAPI, openDriverPdfDownloadUrl, resolveDriverDownloadUrl } from './config';
import './DriverTripInstruction.css';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog';

export function DispatchSheetPreview({ sheet }) {
  const viewport = useRef(null);
  const pages = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 330, height: 794 });
  useEffect(() => {
    const measure = () => setDimensions({ width: viewport.current?.clientWidth || 330, height: pages.current?.offsetHeight || 794 });
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (viewport.current) observer?.observe(viewport.current);
    if (pages.current) observer?.observe(pages.current);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [sheet]);
  if (!sheet) return null;
  const scale = expanded ? 1 : Math.min(1, dimensions.width / 1122.52);
  return <div className="min-w-0 w-full">
    <div className="flex justify-between items-center mb-2 gap-2 text-sm">
      <span className="text-muted-foreground">A4横・帳票プレビュー</span>
      <button type="button" className="rounded-lg border border-[#d8b769]/25 px-3 py-2 text-[#f6d982]"
        onClick={() => setExpanded((current) => !current)}>{expanded ? '全体表示' : '拡大表示'}</button>
    </div>
    <div ref={viewport} className="dispatch-sheet-scroll" data-testid="dispatch-sheet-preview">
      <div style={{ width: 1122.52 * scale, height: dimensions.height * scale, position: 'relative' }}>
        <div ref={pages} className="dispatch-sheet-pages" style={{ transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <article className="dispatch-sheet-paper" aria-label="配車指示書 帳票">
            <h2 className="dispatch-sheet-heading">配車指示書</h2>
            <div className="dispatch-sheet-date">印刷日：{sheet.date}</div>
            <div className={`dispatch-sheet-company ${sheet.company_address?'has-address':''}`}>{sheet.company_name}<br />TEL：{sheet.company_phone}{sheet.company_address?<span className="dispatch-sheet-address">{sheet.company_address}</span>:<br/>}担当：配車担当</div>
            <div className="dispatch-sheet-booking"><span>予約番号</span><strong>{sheet.order_no}</strong><p>{sheet.notice}</p></div>
            <table className="dispatch-sheet-grid" aria-label="配車明細">
              <colgroup>{Array.from({ length: 20 }, (_, index) => <col key={index} style={{ width: '5%' }} />)}</colgroup>
              <tbody>{sheet.rows.map((row, index) => <tr key={index} style={{ height: `${row.height}mm` }}>
                {row.cells.map((cell, cellIndex) => <td key={cellIndex} colSpan={cell.span} rowSpan={cell.rowspan}
                  style={{ textAlign: cell.align === 'C' ? 'center' : 'left', verticalAlign: cell.align === 'C' ? 'middle' : 'top' }}>
                  <div className="dispatch-sheet-cell">{cell.lines.map((line, lineIndex) => <div key={lineIndex}
                    className={line.size === 7 ? 'dispatch-sheet-original' : ''} style={{ fontSize: `${line.size}pt` }}>{line.text || '\u00a0'}</div>)}</div>
                </td>)}
              </tr>)}</tbody>
            </table>
            <div className="dispatch-sheet-confirmation">
              <p>{sheet.payment_note}</p>
              <div><p>{sheet.signature_note}</p><table aria-label="確認署名"><thead><tr><th>日時</th><th>氏名</th></tr></thead><tbody><tr><td>&nbsp;</td><td>&nbsp;</td></tr></tbody></table></div>
            </div>
            <footer><span className="dispatch-document-number">{sheet.document_number}</span>配車指示書　1{sheet.is_vip_plan && <span className="dispatch-plan-mark">VIP-计划 <img src={driverLogo} alt="BIG TAXI" /></span>}</footer>
          </article>
          {sheet.appendix.map((appendix, index) => <article className="dispatch-sheet-appendix" key={index}>
            <h2>配車指示書　別紙</h2><p>予約番号：{sheet.order_no}</p><h3>{appendix.title}（全文）</h3>
            <div>{appendix.blocks.map((block, blockIndex) => <p key={blockIndex} className={block.size === 7 ? 'dispatch-sheet-original' : ''} style={{ fontSize: `${block.size}pt` }}>{block.text}</p>)}</div>
            <footer><span className="dispatch-document-number">{sheet.document_number}</span>{sheet.is_vip_plan && <span className="dispatch-plan-mark">VIP-计划 <img src={driverLogo} alt="BIG TAXI" /></span>}</footer>
          </article>)}
        </div>
      </div>
    </div>
  </div>;
}

export default function DriverTripInstruction({ orderId, sourceType = "official" }) {
  const [open, setOpen] = useState(false);
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  useEffect(() => () => { requestId.current += 1; }, []);

  const load = async () => {
    const id = ++requestId.current;
    setLoading(true);
    setDocument(null);
    setError('');
    try {
      const result = await DriverAPI.getTripInstruction(orderId, sourceType);
      if (requestId.current === id) setDocument(result);
    } catch {
      if (requestId.current === id) setError('配車指示書を取得できませんでした。通信状態と予約の閲覧権限を確認して、再試行してください。');
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  };
  const download = async () => {
    setDownloading(true);
    setError('');
    try {
      const result = await DriverAPI.createTripInstructionPdfLink(orderId, document.revision, sourceType);
      await openDriverPdfDownloadUrl(resolveDriverDownloadUrl(result.download_url));
    } catch (err) {
      setError(err?.response?.status === 409
        ? '予約内容が変更されました。「再読み込み」で最新の指示書を確認してください。'
        : 'PDFを開けませんでした。通信状態を確認して、もう一度お試しください。');
    } finally {
      setDownloading(false);
    }
  };

  return <>
    <button type="button" className="w-full min-h-[62px] rounded-[24px] border border-[#d8b769]/35 bg-[#d8b769]/10 px-4 py-3 font-black text-[#f6d982] flex items-center justify-center gap-2"
      data-testid="driver-trip-instruction-button" onClick={() => { setOpen(true); load(); }}>
      <FileText size={18} aria-hidden="true" />配車指示書
    </button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent lang="ja" className="w-[calc(100%-2rem)] max-w-5xl max-h-[85dvh] overflow-y-auto rounded-xl border-[#d8b769]/25 bg-[#0c0a07] text-white p-4 sm:p-6">
        <DialogTitle>配車指示書</DialogTitle>
        <DialogDescription>日本語の指示と原文をご確認ください。</DialogDescription>
        <div className="flex gap-2">
          <button type="button" onClick={load} disabled={loading || downloading}
            className="flex-1 rounded-xl border border-[#d8b769]/25 py-3 font-semibold disabled:opacity-50">再読み込み</button>
          <button type="button" onClick={download} disabled={!document || loading || downloading}
            className="flex-1 rounded-xl bg-gradient-to-br from-[#d8b769] to-[#f6d982] text-[#0c0a07] py-3 font-semibold disabled:opacity-50">
            {downloading ? 'PDFを準備中…' : 'PDFをダウンロード'}
          </button>
        </div>
        {loading && <p role="status" className="py-6 text-center text-muted-foreground">配車指示書を準備しています…</p>}
        {error && <p role="alert" className="text-sm text-destructive whitespace-pre-wrap">{error}</p>}
        {document?.translation_warning && <p role="alert" className="rounded-xl bg-[#d8b769]/10 p-3 text-sm text-[#f6d982]">{document.translation_warning}</p>}
        {document?.plans_before && document.vip_plans?.map(plan => <DispatchSheetPreview key={plan.plan_day} sheet={plan.sheet} />)}
        {document && <DispatchSheetPreview sheet={document.sheet} />}
        {!document?.plans_before && document?.vip_plans?.map(plan => <DispatchSheetPreview key={plan.plan_day} sheet={plan.sheet} />)}
        <button type="button" onClick={() => setOpen(false)} className="w-full rounded-xl border border-[#d8b769]/25 py-3 font-semibold">閉じる</button>
      </DialogContent>
    </Dialog>
  </>;
}
